import { Injectable } from '@angular/core';
import { createSelector, Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { combineLatest, Observable, Subscription } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  publishReplay,
  refCount
} from 'rxjs/operators';

import {
  LoadAnalytics,
  SelectAnalyticsDuration,
  OverrideAnalyticsCoupon,
  LoadBidAnalytics
} from '../state/analytics/actions';
import { selectAnalyticsState } from '../state/reducer';
import {
  AnalyticsDurationIdentifier,
  DealAnalytics
} from '../types/analytics.types';
import { DealType, DealVersion } from '../types/deal.types';
import { DealFacade } from './deal.facade';

const selectLatestAnalytics = createSelector(
  selectAnalyticsState,
  (state) => state.latest
);

const selectSelectedDuration = createSelector(
  selectAnalyticsState,
  (state) => state.selectedDuration
);

const selectCouponOverrides = createSelector(
  selectAnalyticsState,
  (state) => state.overriddenCoupons
);

export interface AnalyticsFacadeState {
  data: DealAnalytics;
  loading: boolean;
  selectedDuration: AnalyticsDurationIdentifier;
}

export type LatestAnalyticsState = Observable<AnalyticsFacadeState>;

@Injectable({
  providedIn: 'root'
})
export class AnalyticsFacade {
  private readonly dealId$: Observable<string> = this.dealFacade.dealState$.pipe(
    map((state) => state.dealId)
  );

  private readonly deal$: Observable<DealVersion> = this.dealFacade.dealState$.pipe(
    map((state) => state.deal)
  );

  private readonly couponOverrides$ = this.store.pipe(
    map(selectCouponOverrides)
  );

  private readonly latestAnalytics$ = this.store.pipe(
    map(selectLatestAnalytics)
  );

  private readonly selectedDuration$ = this.store.pipe(
    map(selectSelectedDuration)
  );

  public readonly analyticsLoader$: Observable<
    [{ [key: string]: number }, DealVersion]
  > = combineLatest([this.couponOverrides$, this.deal$]).pipe(
    distinctUntilChanged(isEqual)
  );

  public readonly analitycsLoaderSubscription: Subscription = this.analyticsLoader$.subscribe(
    ([overrides, deal]) => {
      if (deal && deal.id) {
        if (deal.dealType === DealType.negotiated) {
          console.log('Loading Analytics');
          this.store.dispatch(
            new LoadAnalytics({ dealId: deal.id, overrides })
          );
        } else {
          console.log('Loading Bid Analytics');
          this.store.dispatch(
            new LoadBidAnalytics({ dealId: deal.id, overrides })
          );
        }
      }
    }
  );

  public readonly latestState$: LatestAnalyticsState = combineLatest([
    this.couponOverrides$,
    this.latestAnalytics$,
    this.selectedDuration$,
    this.dealId$
  ]).pipe(
    distinctUntilChanged(isEqual),
    map(([_, analytics, selectedDuration]) => ({
      data: analytics.data,
      loading: analytics.loading,
      selectedDuration
    })),
    publishReplay(1),
    refCount()
  );

  constructor(
    private dealFacade: DealFacade,
    private store: Store<Record<string, unknown>>
  ) {}

  public selectEffectiveDuration(): void {
    const identifier = AnalyticsDurationIdentifier.Effective;
    this.store.dispatch(new SelectAnalyticsDuration({ identifier }));
  }

  public selectModifiedDuration(): void {
    const identifier = AnalyticsDurationIdentifier.Modified;
    this.store.dispatch(new SelectAnalyticsDuration({ identifier }));
  }

  public overrideAnalyticsCoupon(maturityId: string, coupon: number): void {
    const couponMap = {};
    couponMap[maturityId] = coupon;
    this.store.dispatch(new OverrideAnalyticsCoupon(couponMap));
  }
}
